import React, { useCallback, useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import type { User } from "firebase/auth";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { firebaseAuth } from "../../components/firebase/auth";
import { shopAdminApi, ShopApiError, type OperatingSettingsSnapshot } from "./webshop/shopAdminApi";
import { validOperatingPolicy, type OperatingPolicyDraft } from "./webshop/operatingPolicyModel";
import { useConfirmedShopMutation } from "./webshop/useConfirmedShopMutation";
import WebshopKustomRecovery from "./webshop/WebshopKustomRecovery";
import WebshopMailInspection from "./webshop/WebshopMailInspection";
import PolicyHistory from "./webshop/PolicyHistory";
import "./dashboard.css";
import "./webshopSettings.css";
import "./webshop/webshopCatalog.css";

function PolicyEditor({ user, closeOnly, onClose, onSaved }:
  { user: User; closeOnly: boolean; onClose: () => void; onSaved: () => void }) {
  const [snapshot, setSnapshot] = useState<OperatingSettingsSnapshot | null>(null);
  const [draft, setDraft] = useState<OperatingPolicyDraft | null>(null);
  const [reason, setReason] = useState("");
  const [readError, setReadError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const mutation = useConfirmedShopMutation(onSaved, user?.uid);
  useEffect(() => {
    let live = true; setSnapshot(null); setDraft(null); setReason(""); setReadError("");
    shopAdminApi.settings(user).then((value) => { if (live) { setSnapshot(value); setDraft(value.draft); } })
      .catch(() => { if (live) setReadError("Aktuell driftpolicy kunde inte hämtas. Kontrollera behörighet och anslutning."); });
    return () => { live = false; };
  }, [user, refresh]);
  const locked = mutation.pending || mutation.conflict;
  const change = (patch: Partial<OperatingPolicyDraft>) => setDraft((current) => current ? { ...current, ...patch,
    ...(Object.keys(patch).some((field) => !["status", "checks"].includes(field)) ?
      { status: "draft", checks: { dispatchReviewed: false, shelfLifeReviewed: false, termsReviewed: false } } : {}) } : current);
  const submit = () => {
    if (!snapshot || !reason.trim() || (!closeOnly && !validOperatingPolicy(draft))) return;
    const body = { expectedRevision: snapshot.revision, reason: reason.trim() };
    const original = draft ? structuredClone(draft) : null;
    void mutation.submit((key) => closeOnly ? shopAdminApi.closeCheckout(user, body, key) :
      shopAdminApi.updateSettings(user, { ...body, draft: original! }, key));
  };
  return <Dialog open fullWidth maxWidth="sm" onClose={mutation.pending ? undefined : onClose} disableEscapeKeyDown={mutation.pending}>
    <DialogTitle>{closeOnly ? "Stäng kassan" : "Redigera driftpolicy"}</DialogTitle>
    <DialogContent className="WebshopOfferDialog">
      <Alert severity="info">{closeOnly ? "Stänger nya köp. Befintliga order och reservationer behålls och behöver fortsatt hantering." :
        "Sparandet stänger nya köp. En granskad policy öppnar inte kassan eller låser lager; lanseringskontroller återstår."}</Alert>
      {(readError || mutation.error) && <Alert severity="error">{readError || mutation.error}</Alert>}
      {!snapshot && !readError && <p role="status">Hämtar aktuell driftpolicy…</p>}
      {snapshot && !closeOnly && !draft && <Alert severity="error">Sparad policy är ogiltig och behöver kontrollerad datagranskning. Kassan kan fortfarande stängas.</Alert>}
      {draft && !closeOnly && <>
        <TextField select label="Policystatus" value={draft.status} disabled={locked} onChange={(event) => change({ status: event.target.value as OperatingPolicyDraft["status"] })}>
          <MenuItem value="draft">Utkast</MenuItem><MenuItem value="reviewed">Granskad</MenuItem>
        </TextField>
        {([['checkoutPolicyVersion', 'Driftpolicyversion', 128], ['shopOrigin', 'Webshopens HTTPS-origin', 2000],
          ['termsUrl', 'Köpvillkorens HTTPS-adress', 2000], ['termsVersion', 'Köpvillkorens version', 80]] as const).map(([field, label, maxLength]) =>
          <TextField key={field} label={label} value={draft[field] ?? ""} disabled={locked} inputProps={{ maxLength }}
            onChange={(event) => change({ [field]: event.target.value.trim() || null })} />)}
        <div className="WebshopShippingFields">{([['dispatchLeadDays', 'Dagar till planerad avsändning', 30],
          ['minRemainingShelfLifeDays', 'Minsta återstående hållbarhet vid avsändning (dagar)', 3650]] as const).map(([field, label, max]) =>
          <TextField key={field} type="number" label={label} value={draft[field] ?? ""} disabled={locked} inputProps={{ min: 0, max, step: 1 }}
            helperText="Lämna tomt tills beslutet är fattat. Noll är ett uttryckligt val."
            onChange={(event) => change({ [field]: event.target.value === "" ? null : Number(event.target.value) })} />)}</div>
        {([['dispatchReviewed', 'Avsändningsmarginalen är granskad'], ['shelfLifeReviewed', 'Hållbarhetsgränsen är granskad'],
          ['termsReviewed', 'Köpvillkor och versionsnummer är godkända']] as const).map(([field, label]) =>
          <FormControlLabel key={field} label={label} control={<Checkbox checked={draft.checks[field]} disabled={locked}
            onChange={(event) => change({ checks: { ...draft.checks, [field]: event.target.checked } })} />} />)}
        {!validOperatingPolicy(draft) && <Alert severity="warning">Kontrollera versioner, HTTPS-adresser och heltalsdagar. Granskad status kräver alla uppgifter och kontroller; villkoren ska ligga på webshopens origin.</Alert>}
      </>}
      {snapshot && <TextField label="Skäl till ändringen" value={reason} disabled={locked} inputProps={{ maxLength: 500 }} onChange={(event) => setReason(event.target.value)} />}
    </DialogContent>
    <DialogActions className="WebshopContentActions">
      <Button onClick={onClose} disabled={mutation.pending}>Stäng dialog</Button>
      {(readError || mutation.conflict) && <Button disabled={mutation.pending} onClick={() => { mutation.reset(); setRefresh((value) => value + 1); }}>Hämta aktuell policy</Button>}
      {mutation.pending ? <Button onClick={mutation.retry} disabled={mutation.saving}>Försök igen med samma uppgifter</Button> :
        <Button variant="contained" onClick={submit} disabled={!snapshot || !reason.trim() || (!closeOnly && !validOperatingPolicy(draft)) || mutation.conflict || mutation.saving}>
          {closeOnly ? "Bekräfta stängning" : "Spara policy"}</Button>}
    </DialogActions>
  </Dialog>;
}

function SettingsBody({ user, canManage, previewOnly }: { user: User | null | undefined; canManage: boolean; previewOnly: boolean }) {
  const location = useLocation(), navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<OperatingSettingsSnapshot | null>(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [editor, setEditor] = useState<"policy" | "close" | null>(null);
  const [saved, setSaved] = useState(false);
  const [denied, setDenied] = useState(false), [historyRefresh, setHistoryRefresh] = useState(0);
  const mailDenied = useCallback(() => {
    setDenied(true); setSnapshot(null); setEditor(null); setSaved(false);
  }, []);
  useEffect(() => {
    if (!user || previewOnly) return;
    let live = true; setSnapshot(null); setError("");
    shopAdminApi.settingsStatus(user).then((value) => { if (live) setSnapshot(value); })
      .catch((failure) => { if (live) {
        setError("Driftstatus kunde inte hämtas. Kontrollera API-anslutning och behörighet.");
        if (failure instanceof ShopApiError && [401, 403].includes(failure.status)) setDenied(true);
      } });
    return () => { live = false; };
  }, [user, previewOnly, refresh]);
  const flag = (value: boolean | null) => value === null ? "Okänd" : value ? "Ja" : "Nej";
  return <div className="DashBoardTabInner FreshDashboardTab WebshopSettingsTab">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Webshop</p><h2 className="FreshTabTitle">Webshop inställningar</h2>
      <p className="FreshTabSummary">Driftpolicy, kassastopp och status för Kustom Checkout och Shipping Assistant.</p></div>
      {!previewOnly && <Button disabled={!!editor} onClick={() => { setDenied(false); setRefresh((value) => value + 1); }}>Uppdatera status</Button>}
    </div>
    {previewOnly ? <Alert severity="info">Driftpolicy blir tillgänglig när det skyddade API:t är driftsatt. Förhandsvyn kan inte spara eller öppna köp.</Alert> : <>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !snapshot && <p role="status">Hämtar driftstatus…</p>}
      {saved && <Alert severity="success">Ändringen är registrerad. Nya köp är avstängda; befintligt lager och order behålls.</Alert>}
      {denied && <Alert severity="error">Inställningsbehörigheten behöver verifieras igen. Uppdatera status för en ny skyddad läsning.</Alert>}
      {snapshot && !denied && <>
        <Alert severity={snapshot.release.effectiveCheckoutEnabled ? "warning" : "info"}>{snapshot.release.effectiveCheckoutEnabled ? "Kassan är öppen." : "Kassan är stängd."}
          {!snapshot.release.implementationReady && " Implementationens lanseringsspärr är kvar; den kan inte öppnas här."}</Alert>
        <div className="WebshopSettingsGrid"><section className="WebshopSettingsCard WebshopSettingsBody" aria-labelledby="policy-summary">
          <h3 id="policy-summary">Driftpolicy</h3>
          <p>{snapshot.exists ? `Version ${snapshot.revision}` : "Ingen sparad policy"} · {snapshot.draft?.status === "reviewed" ? "Granskad" : "Ej granskad"}</p>
          {!snapshot.draft && <Alert severity="error">Ogiltiga policyvärden behöver granskas. Stängning är fortsatt tillgänglig.</Alert>}
          <p>Planerad avsändning: {snapshot.draft?.dispatchLeadDays ?? "inte beslutat"} dagar</p>
          <p>Återstående hållbarhet: {snapshot.draft?.minRemainingShelfLifeDays ?? "inte beslutat"} dagar</p>
          <p>Begärd kassaflagga: {flag(snapshot.release.checkoutRequested)} · Lagerlåset: {flag(snapshot.release.inventoryWritesLocked)}</p>
          {canManage && <div className="WebshopConnectionControls">
            <Button variant="outlined" disabled={!!editor || !snapshot.draft} onClick={() => { setSaved(false); setEditor("policy"); }}>Redigera driftpolicy</Button>
            <Button variant="contained" disabled={!!editor} onClick={() => { setSaved(false); setEditor("close"); }}>Stäng kassan</Button>
          </div>}
        </section><section className="WebshopSettingsCard WebshopSettingsBody" aria-labelledby="provider-summary">
          <h3 id="provider-summary">Kustom och leverans</h3>
          <p>Miljö: {snapshot.configuration.environment} · Kustom: {snapshot.configuration.kustomEnvironment ?? "inte konfigurerat"}</p>
          <p>MID: {snapshot.configuration.merchantId ?? "inte konfigurerat"}</p>
          <Alert severity="warning">Betalmetoder och API-anslutning är inte verifierade. KSA-tjänster, priser, moms och bokning behöver Playground-bevis. E-postleverans återstår.</Alert>
          <p>E-postsändning: {snapshot.configuration.emailDelivery === "disabled" ? "Avstängd"
            : snapshot.configuration.emailDelivery === "not_configured" ? "Konfiguration saknas" : "Konfigurerad; faktisk leverans är inte verifierad"}.</p>
          <p><Link to="/webshop/shipping">Granska packprofiler och förpackningar</Link></p>
          <p><a href="https://portal.kustom.co/" target="_blank" rel="noopener noreferrer">Öppna Kustom Portal</a></p>
          <p>Konton och hemligheter hanteras på servern och i Kustom Portal.</p>
        </section></div>
      </>}
    </>}
    {user && canManage && !previewOnly && !denied && <>
      <PolicyHistory user={user} canRead refreshVersion={historyRefresh} onDenied={() => {
        setDenied(true); setSnapshot(null); setEditor(null); setSaved(false);
      }} />
      <WebshopKustomRecovery key={user.uid} user={user} />
      <WebshopMailInspection user={user} onDenied={mailDenied} />
    </>}
    <section className="WebshopSettingsCard WebshopSettingsBody"><h3>Amazon — senare</h3><p>Orderimport är utanför första lanseringen och är inte ansluten.</p></section>
    {editor && user && canManage && !previewOnly && !denied && <PolicyEditor user={user} closeOnly={editor === "close"} onClose={() => setEditor(null)}
      onSaved={() => {
        const search = new URLSearchParams(location.search); search.delete("policyHistoryCursor");
        navigate(`${location.pathname}${search.toString() ? `?${search}` : ""}`);
        setEditor(null); setSaved(true); setRefresh((value) => value + 1); setHistoryRefresh((value) => value + 1);
      }} />}
  </div>;
}

export default function DashboardWebshopSettings({ canManage = false, previewOnly = false }: { canManage?: boolean; previewOnly?: boolean }) {
  const [user] = useAuthState(firebaseAuth);
  return <SettingsBody key={`${user?.uid}:${canManage}:${previewOnly}`} user={user} canManage={canManage} previewOnly={previewOnly} />;
}
